<script lang="ts">
  // Progress through a multi-step flow (sign-up, onboarding, wizards).
  //   <Stepper total={4} current={1} />                  segments; step 2 of 4 (0-based `current`)
  //   <Stepper total={3} current={0} labels={['Account', 'Werk', 'Klaar']} />
  //   <Stepper total={5} current={2} variant="dots" />
  // Done steps are filled, the current one half (or full with `partial={false}`).
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    total: number;
    /** 0-based index of the current step. */
    current: number;
    variant?: 'segments' | 'dots';
    labels?: string[];
    /** Show the current step as half filled (segments). */
    partial?: boolean;
    /** Show "Step 2 of 4" under the bar. */
    showCount?: boolean;
    class?: string;
  }
  let { total, current, variant = 'segments', labels, partial = true, showCount = false, class: cls = '' }: Props = $props();
  const steps = $derived(Array.from({ length: Math.max(0, total) }, (_, i) => i));
  const countLabel = $derived(t('ui.step_of', { current: Math.min(total, current + 1), total }));
</script>

<div class="stepper {variant} {cls}" role="group" aria-label={countLabel}>
  <ol class="track" aria-hidden={labels ? undefined : 'true'}>
    {#each steps as i (i)}
      <li class="step" class:done={i < current} class:current={i === current} class:partial aria-current={i === current ? 'step' : undefined}>
        <span class="bar"></span>
        {#if labels?.[i]}<span class="lbl">{labels[i]}</span>{/if}
      </li>
    {/each}
  </ol>
  {#if showCount}<span class="count">{countLabel}</span>{/if}
</div>

<style>
  .stepper { display: flex; flex-direction: column; gap: var(--space-1_5); min-width: 0; }
  .track { list-style: none; margin: 0; padding: 0; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: var(--space-1_5); }
  .step { display: flex; flex-direction: column; gap: var(--space-1_5); min-width: 0; }
  .bar { position: relative; height: 4px; border-radius: var(--radius-full); background: var(--surface-3); overflow: hidden; }
  .bar::after {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--brand);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform var(--dur-slow) var(--ease);
  }
  .done .bar::after { transform: scaleX(1); }
  .current .bar::after { transform: scaleX(1); }
  .current.partial .bar::after { transform: scaleX(0.5); }
  .lbl { font-size: var(--text-xs); color: var(--text-3); font-weight: var(--weight-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .done .lbl { color: var(--text-2); }
  .current .lbl { color: var(--brand); font-weight: var(--weight-semibold); }
  .count { font-size: var(--text-xs); color: var(--text-3); }

  /* dots */
  .dots .track { display: flex; gap: var(--space-1_5); }
  .dots .step { flex: 0 0 auto; }
  .dots .bar { width: 8px; height: 8px; transition: width var(--dur) var(--ease); }
  .dots .current .bar { width: 22px; }
  .dots .current.partial .bar::after { transform: scaleX(1); }
  .dots .lbl { display: none; }
</style>
